import Vue from 'vue';
import VueRouter, { RouteConfig, Route } from 'vue-router';
import store from '../store';
import Component from 'vue-class-component';
import { Hypotheek } from '@/model/Hypotheek';
import { authService } from '../services/AuthService';
import AppRoutingHookViewVue from '@/views/shared/routing-hook/AppRoutingHookView.vue';
import AppSiteLayoutVue from '@/views/layouts/site/AppSiteLayout.vue';
import AppSessionTimeoutVue from '@/views/shared/AppSessionTimeout.vue';
import jwtDecode from 'jwt-decode';

Vue.use(VueRouter);

// register the router hooks with their names
Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteLeave',
  'beforeRouteUpdate' // for vue-router 2.2+
]);

const routes: Array<RouteConfig> = [
  {
    path: '/',
    component: AppSiteLayoutVue,
    children: [
      {
        path: '',
        component: AppSessionTimeoutVue,
        children: [
          {
            path: '',
            component: AppRoutingHookViewVue,
            meta: {
              requiresAuth: true
            },
            children: [
              {
                // admin user
                path: 'admin',
                component: AppRoutingHookViewVue,
                beforeEnter: (to: Route, from: Route, next: Function) => {
                  authService.isAdmin().then(isAdmin => {
                    if (!isAdmin) {
                      authService.isCustomer().then(isCustomer => {
                        if (isCustomer) {
                          return next({ name: 'hypotheek' });
                        }
                        return next({ name: 'api-service-error', query: { status: '404' } });
                      });
                    } else {
                      next();
                    }
                  });
                },
                children: [
                  {
                    path: 'settings',
                    name: 'settings',
                    component: () => import('../views/admin/AppSettings.vue'),
                    meta: {
                      title: 'Settings'
                    }
                  },
                  {
                    path: '',
                    redirect: '/admin/settings'
                  }
                ]
              },
              {
                // customer user
                path: '',
                component: AppRoutingHookViewVue,
                beforeEnter: (to: Route, from: Route, next: Function) => {
                  authService.isCustomer().then(isCustomer => {
                    if (!isCustomer) {
                      authService.isAdmin().then(isAdmin => {
                        if (isAdmin) {
                          return next({ name: 'settings' });
                        }
                        return next({ name: 'api-service-error', query: { status: '404' } });
                      });
                    } else {
                      next();
                    }
                  });
                },
                children: [
                  {
                    path: 'gebruiker',
                    component: AppRoutingHookViewVue,
                    children: [
                      {
                        path: '',
                        name: 'gebruiker-gegevens',
                        component: () => import('../views/gebruiker/AppGebruikerGegevens.vue'),
                        meta: {
                          title: 'Uw gegevens'
                        }
                      },
                      {
                        path: 'email',
                        name: 'gebruiker-update-email',
                        component: () => import('../views/gebruiker/AppGebruikerEditEmail.vue'),
                        meta: {
                          title: 'E-mailadres wijzigen'
                        }
                      },
                      {
                        path: 'telefoonnummer',
                        name: 'gebruiker-update-telefoonnummer',
                        component: () => import('../views/gebruiker/AppGebruikerEditTelefoonnummer.vue'),
                        meta: {
                          title: 'Telefoonnummer wijzigen'
                        }
                      },
                      {
                        path: 'verificatie',
                        name: 'gebruiker-verificatie-email',
                        component: () => import('../views/gebruiker/AppGebruikerVerificatieEmail.vue'),
                        meta: {
                          title: 'E-mailadres verifiëren'
                        }
                      }
                    ]
                  },
                  {
                    path: '',
                    component: AppRoutingHookViewVue,
                    beforeEnter: (to: Route, from: Route, next: Function) => {
                      Vue.$log.debug('beforeEnter hypotheken');
                      store.dispatch('berichten/getBerichten', true);
                      store.dispatch('hypotheken/getHypotheken', true).then((hypotheken: Hypotheek[]) => {
                        // Wait till getHypotheken has finished before getting betalingen
                        // Maybe better if API fetches hypotheken when not yet cached

                        const openBetaling = hypotheken.find(h => h.openBetaling)?.openBetaling;
                        if (openBetaling) {
                          next({
                            name: 'betaling-status',
                            params: { betalingId: openBetaling.betalingId },
                            replace: true
                          });
                        } else if (hypotheken.length === 0 && to.name !== 'hypotheek-notfound') {
                          next({
                            name: 'hypotheek-notfound'
                          });
                        } else {
                          next();
                        }
                      });
                    },
                    children: [
                      {
                        path: '',
                        name: 'hypotheek',
                        props: true,
                        component: () => import('../views/hypotheken/hypotheek/AppHypotheek.vue'),
                        meta: {
                          title: 'Uw hypotheek'
                        }
                      },
                      {
                        path: 'notfound',
                        name: 'hypotheek-notfound',
                        component: () => import('../views/hypotheken/notfound/HypotheekNotFound.vue'),
                        meta: {
                          title: ''
                        }
                      },
                      {
                        path: 'aflossen',
                        component: AppRoutingHookViewVue,
                        beforeEnter: (to: Route, from: Route, next: Function) => {
                          Vue.$log.debug('beforeEnter aflossen');
                          store.dispatch('hypotheken/getHypotheken', false).then((hypotheken: Hypotheek[]) => {
                            Vue.$log.debug('beforeEnter aflossen', hypotheken);
                            const hypotheek = hypotheken.find(
                              h => h.id === store.state?.hypotheken?.currentHypotheeknummer
                            );
                            if (!hypotheek) {
                              return next({
                                name: 'hypotheek-notfound'
                              });
                            } else {
                              const hypotheekdelen = hypotheek.aflosbareHypotheekdelen;
                              let currentHypotheekdeel = hypotheekdelen.find(
                                d => d.hypotheeknummer === store.state?.hypotheken?.currentHypotheekdeelnummer
                              );
                              if (hypotheekdelen.length == 0) {
                                return next({
                                  name: 'hypotheek'
                                });
                              } else if (
                                (currentHypotheekdeel || hypotheekdelen.length === 1) &&
                                to.name !== 'aflossen-hypotheekdeel'
                              ) {
                                currentHypotheekdeel = currentHypotheekdeel || hypotheekdelen[0];
                                store.commit(
                                  'hypotheken/selectCurrentHypotheekdeelnummer',
                                  currentHypotheekdeel.hypotheeknummer
                                );
                                return next({
                                  name: 'aflossen-hypotheekdeel',
                                  query: to.query,
                                  replace: true
                                });
                              }
                            }
                            next();
                          });
                        },
                        children: [
                          {
                            path: '',
                            name: 'aflossen-list',
                            component: () =>
                              import('../views/hypotheken/aflossen/list/AppAflossenHypotheekdeelList.vue'),
                            meta: {
                              title: 'Uw extra aflossing'
                            }
                          },
                          {
                            path: '',
                            name: 'aflossen-hypotheekdeel',
                            component: () =>
                              import('../views/hypotheken/aflossen/hypotheekdeel/AppAflossenHypotheekdeel.vue'),
                            meta: {
                              title: 'Uw extra aflossing'
                            }
                          }
                        ]
                      },
                      {
                        path: 'berichten',
                        name: 'berichten',
                        component: () => import('../views/hypotheken/berichten/AppBerichten.vue'),
                        meta: {
                          title: 'Uw berichten'
                        }
                      },
                      {
                        path: 'bouwdepot',
                        name: 'bouwdepot',
                        component: () => import('../views/hypotheken/bouwdepot/AppBouwdepot.vue'),
                        meta: {
                          title: 'Declaratie bouwdepot'
                        }
                      },
                      {
                        path: 'wijzigen',
                        name: 'wijzigen',
                        props: true,
                        component: () => import('../views/hypotheken/wijzigen/AppWijzigen.vue'),
                        meta: {
                          title: 'Wijzigingen'
                        }
                      },
                      {
                        path: 'rekeningnummer',
                        name: 'rekeningnummer',
                        component: () => import('../views/hypotheken/rekeningnummer/AppRekeningnummer.vue'),
                        meta: {
                          title: 'Rekeningnummer wijzigen'
                        }
                      },
                      {
                        path: 'taxatierapport',
                        name: 'taxatierapport',
                        component: () => import('../views/hypotheken/taxatierapport/AppTaxatierapport.vue'),
                        meta: {
                          title: 'Woningwaarde wijzigen'
                        }
                      }
                    ]
                  },
                  {
                    path: 'betalingstatus/:betalingId',
                    name: 'betaling-status',
                    component: () => import('../views/betalingstatus/AppBetalingStatus.vue'),
                    meta: {
                      title: 'Uw extra aflossing',
                      menu: false
                    }
                  }
                ]
              }
            ]
          },
          {
            path: '',
            component: AppRoutingHookViewVue,
            children: [
              {
                path: 'verify-email',
                name: 'verify-email',
                props: true,
                component: () => import('../views/verify-email/AppVerifyEmail.vue'),
                meta: {
                  title: 'Bevestig uw e-mailadres',
                  menu: false
                }
              },
              {
                path: 'registreer',
                name: 'registreer',
                component: () => import('../views/registreren/AppRegistreren.vue'),
                meta: {
                  title: 'Registreer uw profiel',
                  menu: false
                },
                beforeEnter: (to: Route, from: Route, next: Function) => {
                  const jwt = jwtDecode(to.query.token as string) as any;
                  if (!jwt.email_verified) {
                    next({ name: 'verify-email', params: { authorizeAgain: jwt.authorize_again } });
                  } else {
                    next();
                  }
                }
              },
              {
                path: 'api-service-error',
                name: 'api-service-error',
                component: () => import('../views/api-service-error/ApiServiceError.vue'),
                meta: {
                  title: 'Onze excuses',
                  menu: false
                }
              }
            ]
          }
        ]
      },
      {
        path: 'sessie-afgesloten',
        name: 'sessie-afgesloten',
        component: () => import('../views/sessie-afgesloten/AppSessieAfgesloten.vue'),
        meta: {
          title: 'Uitgelogd',
          menu: false
        }
      }
    ]
  },
  {
    path: '',
    redirect: '/'
  }
];

const router = new VueRouter({
  mode: 'history',
  // base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

router.beforeEach((to: Route, from: Route, next: Function) => {
  Vue.$log.debug('beforeEach', to, from);
  if (to.matched.some(record => record.meta.requiresAuth)) {
    authService
      .isAuthenticated()
      .then(authenticated => {
        if (!authenticated) {
          authService.login({ state: { to: to.fullPath } });
          next(false);
        } else {
          next();
        }
      })
      .catch(e => {
        Vue.$log.error('isAuthenticated error', e);
      });
  } else {
    next(); // make sure to always call next()!
  }
});

let isBackNavigation = false;

router.afterEach((to: Route, from: Route) => {
  Vue.$log.debug('afterEach', to, from);

  const baseTitle = 'Uw Philips Pensioenfonds-hypotheek';
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && (r.meta as any).title);
  if (nearestWithTitle && (nearestWithTitle.meta as any).title) {
    document.title = `${(nearestWithTitle.meta as any).title} - ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }

  // Check if the navigation is a back navigation
  if (isBackNavigation) {
    // If it's a back navigation, do nothing to preserve scroll position
    isBackNavigation = false; // Reset the flag
  } else {
    // Scroll to the top for normal navigation
    window.scrollTo(0, 0);
  }
});

// Listen for popstate event to detect back navigation
window.addEventListener('popstate', () => {
  isBackNavigation = true;
});

export default router;
